/*
  CTA search results page.
  Tabs, pagination and sort swap the results in place through the Section Rendering API
  instead of reloading the page. The other tab is prefetched on load, so switching is
  instant and its count and preview (blog cards or product thumbnails) show up front.
*/
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

  function initPage(root) {
    if (!root || root.dataset.ctaReady) return;
    root.dataset.ctaReady = 'true';

    const sectionId = root.dataset.sectionId;
    const terms = root.dataset.terms;
    const cache = new Map();
    let navToken = 0;

    // ---------- Scroll reveal ----------
    let observer = null;
    const observe = () => {
      if (!observer) return;
      root.querySelectorAll('[data-cta-srp-reveal]:not(.is-in)').forEach((el) => observer.observe(el));
    };
    if (!reduceMotion() && 'IntersectionObserver' in window && !window.Shopify?.designMode) {
      root.classList.add('cta-srp--animate');
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
      observe();
    }

    // ---------- Refine field ----------
    const input = root.querySelector('[data-cta-srp-input]');
    const clear = root.querySelector('[data-cta-srp-clear]');
    if (input && clear) {
      const sync = () => { clear.hidden = !input.value; };
      input.addEventListener('input', sync);
      clear.addEventListener('click', () => {
        input.value = '';
        sync();
        input.focus();
      });
    }

    if (!terms || !sectionId) return;

    // ---------- Section fetches ----------
    const pageKey = (href) => {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('section_id');
      return url.pathname + url.search;
    };

    const load = (href) => {
      const key = pageKey(href);
      if (!cache.has(key)) {
        const url = new URL(key, window.location.origin);
        url.searchParams.set('section_id', sectionId);
        const request = fetch(url, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((html) => {
            const page = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cta-srp]');
            if (!page) throw new Error('No results markup');
            return page;
          });
        request.catch(() => cache.delete(key));
        cache.set(key, request);
      }
      return cache.get(key);
    };

    cache.set(pageKey(window.location.href), Promise.resolve(root.cloneNode(true)));

    // ---------- The other tab: count pill plus its preview ----------
    const fillOtherTab = (page) => {
      const otherTab = page.dataset.tab;
      const link = root.querySelector(`[data-cta-srp-tab="${otherTab}"]`);
      if (!link) return;
      link.removeAttribute('data-pending');
      const count = parseInt(page.dataset.count, 10) || 0;
      if (!count) {
        link.hidden = true;
        return;
      }
      const pill = link.querySelector('[data-cta-srp-pill]');
      if (pill && pill.textContent !== String(count)) {
        pill.textContent = count;
        link.classList.add('is-fetched');
      }

      const preview = page.querySelector('[data-cta-srp-preview]');
      const items = preview ? [...preview.content.children].map((node) => node.cloneNode(true)) : [];

      if (otherTab === 'article') {
        const strip = root.querySelector('[data-cta-srp-blog]');
        if (!strip || !items.length) return;
        strip.querySelector('[data-cta-srp-blog-list]').replaceChildren(...items);
        strip.querySelector('[data-cta-srp-blog-count]').textContent =
          `${plural(count, 'result matches', 'results match')} “${terms}”`;
        strip.hidden = false;
      } else {
        const card = root.querySelector('[data-cta-srp-xlink]');
        if (!card) return;
        card.querySelector('[data-cta-srp-thumbs]').replaceChildren(...items);
        card.querySelector('[data-cta-srp-xlink-title]').textContent =
          `${plural(count, 'product matches', 'products match')} “${terms}”`;
        card.hidden = false;
      }
      observe();
    };

    const prefetchOtherTab = () => {
      const other = root.querySelector('[data-cta-srp-tab][data-pending], [data-cta-srp-tab]:not(.is-active)');
      if (!other) return;
      load(other.href)
        .then(fillOtherTab)
        .catch(() => other.removeAttribute('data-pending'));
    };

    // ---------- Collection chips (predictive search API) ----------
    const chipsWrap = root.querySelector('[data-cta-srp-collections]');
    const loadChips = () => {
      const template = root.querySelector('[data-cta-srp-chip]');
      const predictiveUrl = root.dataset.predictiveUrl;
      if (!chipsWrap || !template || !predictiveUrl) return;
      const params = new URLSearchParams({ q: terms, 'resources[type]': 'collection', 'resources[limit]': '5' });
      fetch(`${predictiveUrl}.json?${params}`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then((json) => {
          const collections = json?.resources?.results?.collections || [];
          if (!collections.length) throw new Error('No collections');
          const chips = collections.map((collection, i) => {
            const li = template.content.firstElementChild.cloneNode(true);
            const link = li.querySelector('a');
            link.href = collection.url;
            link.querySelector('span').textContent = collection.title;
            li.style.setProperty('--i', i);
            return li;
          });
          chipsWrap.querySelector('[data-cta-srp-chips]').replaceChildren(...chips);
          chipsWrap.classList.add('is-ready');
        })
        .catch(() => collapse(chipsWrap));
    };

    const collapse = (el) => {
      if (reduceMotion()) {
        el.classList.add('is-empty');
        return;
      }
      el.style.height = `${el.offsetHeight}px`;
      el.classList.add('is-collapsing');
      requestAnimationFrame(() => {
        el.style.height = '0px';
      });
      el.addEventListener('transitionend', () => {
        el.classList.add('is-empty');
        el.style.height = '';
      }, { once: true });
    };

    // ---------- In-place navigation ----------
    const results = root.querySelector('[data-cta-srp-results]');

    const scrollToBar = () => {
      const target = root.querySelector('[data-cta-srp-bar]') || results;
      const top = target.getBoundingClientRect().top + window.scrollY - 96;
      if (window.scrollY > top) {
        window.scrollTo({ top, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    };

    const swapResults = (page) => {
      const incoming = page.querySelector('[data-cta-srp-results]');
      const nodes = incoming ? [...incoming.childNodes].map((node) => node.cloneNode(true)) : [];
      if (reduceMotion()) {
        results.replaceChildren(...nodes);
        return;
      }
      const from = results.offsetHeight;
      results.style.height = `${from}px`;
      results.classList.add('is-swapping');
      results.replaceChildren(...nodes);
      const to = results.scrollHeight;
      requestAnimationFrame(() => {
        results.style.height = `${to}px`;
      });
      const done = () => {
        results.style.height = '';
        results.classList.remove('is-swapping');
      };
      results.addEventListener('transitionend', done, { once: true });
      window.setTimeout(done, 600);
    };

    const render = (page) => {
      const previousTab = root.dataset.tab;
      root.dataset.tab = page.dataset.tab;
      root.dataset.count = page.dataset.count;

      const label = page.querySelector('[data-cta-srp-count-label]');
      const currentLabel = root.querySelector('[data-cta-srp-count-label]');
      if (label && currentLabel) currentLabel.textContent = label.textContent;

      const typeInput = root.querySelector('[data-cta-srp-type]');
      const nextType = page.querySelector('[data-cta-srp-type]');
      if (typeInput && nextType) typeInput.value = nextType.value;

      // The bar is replaced whole; the old tab's count is carried across
      const nextBar = page.querySelector('[data-cta-srp-bar]');
      const liveBar = root.querySelector('[data-cta-srp-bar]');
      if (nextBar && liveBar) {
        const fresh = nextBar.cloneNode(true);
        fresh.removeAttribute('data-cta-srp-in');
        liveBar.querySelectorAll('[data-cta-srp-tab]').forEach((tab) => {
          const pill = tab.querySelector('[data-cta-srp-pill]')?.textContent;
          const twin = fresh.querySelector(`[data-cta-srp-tab="${tab.dataset.ctaSrpTab}"]`);
          if (!twin || tab.hidden) {
            if (twin) twin.hidden = true;
            return;
          }
          if (pill) twin.querySelector('[data-cta-srp-pill]').textContent = pill;
          twin.removeAttribute('data-pending');
        });
        liveBar.replaceWith(fresh);
      }

      swapResults(page);

      if (previousTab !== page.dataset.tab) {
        document.title = document.title.replace(/\d[\d,]*/, page.dataset.count);
      }

      // The tab we just left is cached, so its count and preview come back instantly
      const previous = root.querySelector('[data-cta-srp-tab]:not(.is-active)');
      if (previous) load(previous.href).then(fillOtherTab).catch(() => {});
      observe();
    };

    const navigate = (href, { push = true, scroll = false } = {}) => {
      const token = ++navToken;
      root.classList.add('is-loading');
      return load(href)
        .then((page) => {
          if (token !== navToken) return;
          if (scroll) scrollToBar();
          render(page);
          if (push) window.history.pushState({ ctaSrp: true }, '', pageKey(href));
        })
        .catch(() => {
          window.location.href = href;
        })
        .finally(() => {
          if (token === navToken) root.classList.remove('is-loading');
        });
    };

    root.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('[data-cta-srp-tab], [data-cta-srp-nav]');
      if (!link || !root.contains(link) || link.target === '_blank') return;
      if (link.matches('[data-cta-srp-tab]') && link.classList.contains('is-active')) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      const isPage = link.closest('.cta-srp__pagination');
      navigate(link.href, { scroll: Boolean(isPage) || link.matches('[data-cta-srp-nav]') });
    });

    // Warm the cache when a pointer heads for a tab or page link
    root.addEventListener('pointerover', (event) => {
      const link = event.target.closest('[data-cta-srp-tab], .cta-srp__pagination [data-cta-srp-nav]');
      if (link && !link.classList.contains('is-active')) load(link.href).catch(() => {});
    });

    root.addEventListener('change', (event) => {
      const form = event.target.closest('[data-cta-srp-sort]');
      if (!form) return;
      const url = new URL(form.action, window.location.origin);
      new FormData(form).forEach((value, key) => url.searchParams.set(key, value));
      navigate(url.toString());
    });

    window.history.replaceState({ ctaSrp: true }, '', window.location.href);
    window.addEventListener('popstate', (event) => {
      if (event.state?.ctaSrp) navigate(window.location.href, { push: false });
    });

    // Chips belong to the query, so they load once and stay across tab switches
    loadChips();

    const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 200));
    idle(prefetchOtherTab);
  }

  const boot = () => document.querySelectorAll('[data-cta-srp]').forEach(initPage);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
